import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface PortalProps {
  children: React.ReactNode;
  container?: HTMLElement;
  /**
   * Optional ID for a portal root element. If provided and the element doesn't exist,
   * it will be created and appended to document.body.
   */
  portalId?: string;
}

/**
 * Portal Component
 *
 * Renders children into a DOM node outside the parent component's DOM hierarchy.
 * This is critical for overlays (dropdowns, modals, tooltips) to prevent z-index conflicts
 * and clipping issues caused by parent containers with overflow:hidden or transform properties.
 *
 * Features:
 * - Waits for DOM to be ready before rendering (prevents "Node cannot be found" errors)
 * - Auto-creates portal container if portalId is specified and element doesn't exist
 * - Falls back to document.body if no container is specified
 * - Returns null during SSR or before mount
 *
 * Usage:
 * ```tsx
 * // Default: renders to document.body
 * <Portal>
 *   <div className="fixed z-dropdown">Dropdown content</div>
 * </Portal>
 *
 * // With specific portal root (auto-created if missing)
 * <Portal portalId="stickies-root">
 *   <StickyPanel />
 * </Portal>
 * ```
 *
 * @param children - React elements to render in portal
 * @param container - Optional custom container element
 * @param portalId - Optional ID for portal root (auto-creates if missing)
 */
export const Portal: React.FC<PortalProps> = ({
  children,
  container,
  portalId
}) => {
  // Use state instead of ref to trigger re-render when container is ready
  const [mounted, setMounted] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Skip if we're in SSR or document isn't available
    if (typeof document === 'undefined') {
      return;
    }

    let targetContainer: HTMLElement | null = null;

    // Priority: explicit container > portalId element > document.body
    if (container) {
      targetContainer = container;
    } else if (portalId) {
      // Look for existing element or create it
      let portalRoot = document.getElementById(portalId);
      if (!portalRoot) {
        // Create the portal root and append to body
        portalRoot = document.createElement('div');
        portalRoot.id = portalId;
        portalRoot.setAttribute('data-portal-root', 'true');
        document.body.appendChild(portalRoot);
      }
      targetContainer = portalRoot;
    } else {
      // Default to document.body
      targetContainer = document.body;
    }

    setPortalContainer(targetContainer);
    setMounted(true);

    // Cleanup: remove auto-created portal roots when unmounted
    return () => {
      if (portalId) {
        const portalRoot = document.getElementById(portalId);
        if (portalRoot && portalRoot.getAttribute('data-portal-root') === 'true' && !portalRoot.hasChildNodes()) {
          // Only remove if we created it and it's empty
          portalRoot.parentNode?.removeChild(portalRoot);
        }
      }
    };
  }, [container, portalId]);

  // Don't render until mounted and container is ready
  if (!mounted || !portalContainer) {
    return null;
  }

  return createPortal(children, portalContainer);
};
